import React from 'react';
import { Calendar } from './DailyDigestComponents';
import CalendarCountdown from './components/CalendarCountdown';

const TodaysGoalsColumn = ({
  sectionRefs,
  getTodaysTasks,
  handleTaskToggle,
  tasks,
  isLoading,
  isAddingTasks,
  activeDays,
  checkAchievements,
  userID,
  onRefreshActiveDays,
}) => {
  // Calculate if today's tasks are completed
  const todaysTasks = getTodaysTasks(tasks);
  const todayCompleted = todaysTasks.length > 0 && todaysTasks.every((task) => task.completed);

  // Handle midnight reset
  const handleMidnightReset = () => {
    console.log('Midnight reset detected, refreshing calendar data...');
    if (onRefreshActiveDays) {
      onRefreshActiveDays();
    }
  };

  return (
    <>
      {/* Column 2: Today's Goals and Active Days */}
      <div className="md:col-span-5 flex flex-col space-y-6 h-full">
        <div
          ref={sectionRefs.goals}
          className="bg-white p-6 rounded-lg shadow-md shrink-0 grow-0"
        >
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">Today's Goals</h2>
              {/* Indicator for personalized tasks */}
              {tasks.some(
                (task) =>
                  typeof task.task_id === 'number' && !String(task.task_id).startsWith('canned_')
              ) && (
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  ✨ Personalized
                </span>
              )}
            </div>
            <div className="flex items-center">
              <div className="w-32 h-2 bg-gray-200 rounded-full mr-2">
                <div
                  className="h-full bg-linear-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-500 ease-in-out"
                  style={{
                    width: `${todaysTasks.length > 0 ? (todaysTasks.filter((task) => task.completed).length / todaysTasks.length) * 100 : 0}%`,
                  }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-600">
                {todaysTasks.filter((task) => task.completed).length}/{todaysTasks.length}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {todaysTasks
              .sort((a, b) => a.task_id - b.task_id)
              .map((task) => (
                <div
                  key={task.task_id}
                  className={`flex items-center p-3 rounded-lg transition-all duration-300 min-h-[56px] ${
                    task.completed
                      ? 'bg-blue-50 border border-blue-100'
                      : 'bg-white border border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="relative mr-4">
                    <input
                      type="checkbox"
                      checked={task.completed}
                      onChange={() => handleTaskToggle(task.task_id)}
                      className="peer absolute opacity-0 w-6 h-6 cursor-pointer"
                      id={`task-${task.task_id}`}
                    />
                    <label
                      htmlFor={`task-${task.task_id}`}
                      className={`flex items-center justify-center w-6 h-6 border-2 rounded-md transition-colors duration-300 ${
                        task.completed ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {task.completed && (
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </label>
                  </div>
                  <div className="flex-1">
                    <a
                      href={task.task_link || '#'}
                      className={`block font-medium transition-all duration-300 ${
                        task.completed ? 'text-gray-500' : 'text-gray-800 hover:text-blue-600'
                      }`}
                    >
                      {task.task_content}
                    </a>
                  </div>
                  <div className="ml-2">
                    <a
                      href={task.task_link || '#'}
                      className="text-blue-500 hover:text-blue-700 transition-colors duration-300"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              ))}
          </div>
          {tasks.length === 0 && !isLoading && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-center">
              <div className="flex items-center justify-center space-x-2">
                <svg
                  className="animate-spin h-4 w-4 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <p className="text-blue-700 font-medium">Generating your personalized tasks...</p>
              </div>
            </div>
          )}
        </div>

        <div
          ref={sectionRefs.calendar}
          className="bg-white p-6 rounded-lg shadow-md overflow-hidden flex-1 flex flex-col"
        >
          <div className="shrink-0">
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-semibold mb-1">Active Days</h2>
              <CalendarCountdown
                todayCompleted={todayCompleted}
                onMidnightReset={handleMidnightReset}
              />
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <Calendar
              activeDays={activeDays}
              checkAchievements={checkAchievements}
              userID={userID}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default TodaysGoalsColumn;
